import { SVG_ASSET_URL, PRODUCT_URL, RECIPES_URL } from '../store/requests';
import { makeUrl } from '../util';

export type SvgAssetListItem = {
    id: string;
    code?: string | null;
    title: string;
    tags: string[];
    svgText?: string;
    byteSizeSvg: number;
    chapdrawFormatVersion: number;
    createdAt: string;
    updatedAt: string;
};

export type SvgAsset = SvgAssetListItem & {
    svgText: string;
    chapdrawBase64?: string | null;
};

export type ProductSvgAsset = {
    svgAssetId: string;
    sortOrder: number;
    isPrimary: boolean;
    role?: string | null;
    title: string;
    tags: string[];
    svgText: string;
    byteSizeSvg: number;
    updatedAt: string;
};

export type ProductSvgLink = {
    svgAssetId: string;
    sortOrder: number;
    isPrimary: boolean;
    role?: string | null;
};

export type SvgAssetWrite = {
    title: string;
    tags?: string[];
    code?: string | null;
    svgText: string;
    chapdrawBase64: string;
    chapdrawFormatVersion: number;
    updatedAt?: string;
};

async function parseJson(response: Response) {
    if (!response.ok) {
        const err = new Error(`HTTP ${response.status}`) as Error & { status?: number };
        err.status = response.status;
        throw err;
    }
    if (response.status === 204) {
        return null;
    }
    const text = await response.text();
    if (!text) {
        return null;
    }
    return JSON.parse(text);
}

export function bytesToBase64(bytes: Uint8Array): string {
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
}

export function base64ToBytes(base64: string): Uint8Array {
    const binary = atob(base64);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        out[i] = binary.charCodeAt(i);
    }
    return out;
}

/**
 * Collapse repeated identical default xmlns on one tag.
 * product-api SanitizeSvgText (encoding/xml) used to emit
 * `xmlns="..." xmlns="..."` which is invalid XML and breaks <img data:>.
 */
export function normalizeSvgForPreview(svgText: string): string {
    return svgText.replace(
        /(\sxmlns="http:\/\/www\.w3\.org\/2000\/svg"){2,}/g,
        ' xmlns="http://www.w3.org/2000/svg"',
    );
}

export function svgPreviewDataUrl(svgText: string): string {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
        normalizeSvgForPreview(svgText),
    )}`;
}

export const svgAssetsService = {
    async list(params: { q?: string; tag?: string } = {}): Promise<SvgAssetListItem[]> {
        const search = new URLSearchParams();
        if (params.q) search.set('q', params.q);
        if (params.tag) search.set('tag', params.tag);
        const qs = search.toString();
        const url = makeUrl([SVG_ASSET_URL]) + (qs ? `?${qs}` : '');
        const response = await fetch(url, { credentials: 'include' });
        return (await parseJson(response)) ?? [];
    },

    async get(assetId: string, includeChapdraw = false): Promise<SvgAsset> {
        const qs = includeChapdraw ? '?includeChapdraw=1' : '';
        const response = await fetch(makeUrl([SVG_ASSET_URL, assetId, '/']) + qs, {
            credentials: 'include',
        });
        return parseJson(response);
    },

    async create(body: SvgAssetWrite): Promise<SvgAsset> {
        const response = await fetch(makeUrl([SVG_ASSET_URL]), {
            credentials: 'include',
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        return parseJson(response);
    },

    async update(assetId: string, body: Partial<SvgAssetWrite>): Promise<SvgAsset> {
        const response = await fetch(makeUrl([SVG_ASSET_URL, assetId, '/']), {
            credentials: 'include',
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        return parseJson(response);
    },

    async remove(assetId: string): Promise<void> {
        const response = await fetch(makeUrl([SVG_ASSET_URL, assetId, '/']), {
            credentials: 'include',
            method: 'DELETE',
        });
        await parseJson(response);
    },

    async listForProduct(productId: string): Promise<ProductSvgAsset[]> {
        const response = await fetch(makeUrl([PRODUCT_URL, productId, '/svg-asset/']), {
            credentials: 'include',
        });
        return (await parseJson(response)) ?? [];
    },

    async replaceForProduct(productId: string, links: ProductSvgLink[]): Promise<ProductSvgAsset[]> {
        const response = await fetch(makeUrl([PRODUCT_URL, productId, '/svg-asset/']), {
            credentials: 'include',
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(links),
        });
        return (await parseJson(response)) ?? [];
    },

    async attachToProduct(productId: string, link: ProductSvgLink): Promise<ProductSvgAsset[]> {
        const response = await fetch(makeUrl([PRODUCT_URL, productId, '/svg-asset/']), {
            credentials: 'include',
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(link),
        });
        return (await parseJson(response)) ?? [];
    },

    async detachFromProduct(productId: string, assetId: string): Promise<void> {
        const response = await fetch(makeUrl([PRODUCT_URL, productId, `/svg-asset/${assetId}/`]), {
            credentials: 'include',
            method: 'DELETE',
        });
        await parseJson(response);
    },

    async listForRecipe(recipeId: string): Promise<ProductSvgAsset[]> {
        const response = await fetch(makeUrl([RECIPES_URL, recipeId, '/svg-asset/']), {
            credentials: 'include',
        });
        return (await parseJson(response)) ?? [];
    },

    async replaceForRecipe(recipeId: string, links: ProductSvgLink[]): Promise<ProductSvgAsset[]> {
        const response = await fetch(makeUrl([RECIPES_URL, recipeId, '/svg-asset/']), {
            credentials: 'include',
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(links),
        });
        return (await parseJson(response)) ?? [];
    },

    async attachToRecipe(recipeId: string, link: ProductSvgLink): Promise<ProductSvgAsset[]> {
        const response = await fetch(makeUrl([RECIPES_URL, recipeId, '/svg-asset/']), {
            credentials: 'include',
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(link),
        });
        return (await parseJson(response)) ?? [];
    },

    async detachFromRecipe(recipeId: string, assetId: string): Promise<void> {
        const response = await fetch(makeUrl([RECIPES_URL, recipeId, `/svg-asset/${assetId}/`]), {
            credentials: 'include',
            method: 'DELETE',
        });
        await parseJson(response);
    },

    downloadUrl(assetId: string, kind: 'svg' | 'chapdraw'): string {
        const suffix = kind === 'svg' ? 'download.svg' : 'download.chapdraw';
        return makeUrl([SVG_ASSET_URL, assetId, `/${suffix}`]);
    },
};
